import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  SSE_ENDPOINT,
  SSE_EVENT,
  sseEventSchema,
  type IngestionStatusDTO,
  type SseEventName,
  type SsePayload,
} from '@bb/shared';

type Listener = (data: unknown) => void;

export type LinkState = 'connecting' | 'open' | 'closed';

type LiveContextValue = {
  linkState: LinkState;
  ingestion: IngestionStatusDTO | null;
  subscribe: (event: SseEventName, listener: Listener) => () => void;
};

const LiveContext = createContext<LiveContextValue | null>(null);

/**
 * Single EventSource for the whole app.
 *
 * The browser handles reconnection and replays `Last-Event-ID` on its own, so
 * there is no retry logic here — the server side of that contract (the `id:`
 * frame carrying `post.seq`) is what makes it work.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const listeners = useRef(new Map<SseEventName, Set<Listener>>());
  const [linkState, setLinkState] = useState<LinkState>('connecting');
  const [ingestion, setIngestion] = useState<IngestionStatusDTO | null>(null);

  const subscribe = useCallback((event: SseEventName, listener: Listener) => {
    const set = listeners.current.get(event) ?? new Set<Listener>();
    set.add(listener);
    listeners.current.set(event, set);
    return () => {
      set.delete(listener);
    };
  }, []);

  useEffect(() => {
    const source = new EventSource(SSE_ENDPOINT, { withCredentials: true });

    source.onopen = () => setLinkState('open');
    source.onerror = () => {
      setLinkState(source.readyState === EventSource.CLOSED ? 'closed' : 'connecting');
    };

    const handlers: Array<[SseEventName, (event: MessageEvent<string>) => void]> = [];

    for (const name of Object.values(SSE_EVENT)) {
      const handler = (event: MessageEvent<string>) => {
        let data: unknown;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        // Validate at the boundary: a payload shape change should surface as a
        // dropped event in the console, never as a half-rendered post.
        const parsed = sseEventSchema.safeParse({ event: name, data });
        if (!parsed.success) {
          console.warn(`Dropped malformed "${name}" event`, parsed.error.issues);
          return;
        }

        if (parsed.data.event === SSE_EVENT.INGESTION_STATUS) {
          setIngestion(parsed.data.data);
        }

        for (const listener of listeners.current.get(name) ?? []) {
          listener(parsed.data.data);
        }
      };

      source.addEventListener(name, handler as EventListener);
      handlers.push([name, handler]);
    }

    return () => {
      for (const [name, handler] of handlers) {
        source.removeEventListener(name, handler as EventListener);
      }
      source.close();
    };
  }, []);

  const value = useMemo<LiveContextValue>(
    () => ({ linkState, ingestion, subscribe }),
    [linkState, ingestion, subscribe],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveContextValue {
  const context = useContext(LiveContext);
  if (!context) throw new Error('useLive must be used inside <LiveProvider>.');
  return context;
}

/** Typed subscription to one event kind. The handler may change freely. */
export function useLiveEvent<N extends SseEventName>(
  event: N,
  handler: (data: SsePayload<N>) => void,
): void {
  const { subscribe } = useLive();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(
    () => subscribe(event, (data) => ref.current(data as SsePayload<N>)),
    [event, subscribe],
  );
}
